/**
 * arbi dm list                              — list DMs (decrypted, filterable)
 * arbi dm send [recipient] [content]        — send an E2E encrypted DM
 * arbi dm read [ids...]                     — mark messages as read
 * arbi dm delete [ids...]                   — delete messages
 *
 * `dm list` supports `--json` for filtering/iteration (the natural list-view
 * use case). `dm send/read/delete` deliberately do NOT — their text output
 * is the canonical confirmation and carries every useful field (notification
 * ID via `ntf-…`, encryption status via `(encrypted)` suffix). Agents grep
 * the text instead of piping `--json` through jq.
 */

import { Command } from 'commander'
import {
  dm as dmOps,
  contacts as contactOps,
  agents as agentOps,
  workspaces as workspaceOps,
} from '@arbidocs/sdk'
import {
  runAction,
  resolveDmCrypto,
  printTable,
  formatUserName,
  truncate,
  requireInteractive,
  printJson,
  suggestSubcommandAndExit,
  dryRun,
  type UserInfo,
} from '../helpers.js'
import { promptSelect, promptCheckbox, promptInput } from '../prompts.js'
import { success, error } from '../output.js'

/**
 * Common shape of one decrypted DM row from the SDK. We type it loosely
 * because the SDK returns `Record<string, unknown>` (it widens the OpenAPI
 * type after stitching in decrypted content).
 */
type DmRow = Record<string, unknown>

/**
 * Resolve a `recipient` arg (email OR `usr-/agt-` ext_id) into the values
 * `sendEncryptedDM` needs: the recipient's external_id and their curve25519
 * public key. Walks three lists in order — contacts, persistent agents,
 * current-workspace users — and stops at the first hit. Returns `null` when
 * nothing matches so callers can render a recovery hint with exit code 3.
 *
 * One helper replaces the previous code that searched twice (once for email
 * input, once for ext_id input) with subtly different ordering, which
 * exposed a `recipientExtId === recipient` parsing bug for ext_id inputs.
 */
export async function resolveRecipient(
  arbi: Awaited<ReturnType<typeof resolveDmCrypto>>['arbi'],
  ref: string
): Promise<{ extId: string; pubKey: string } | null> {
  const isEmail = ref.includes('@')
  const matchKey: 'email' | 'external_id' = isEmail ? 'email' : 'external_id'

  // Contacts: extract the embedded user record's pubkey, fall back to the
  // contact-level external_id when the contact hasn't been registered yet.
  const contacts = await contactOps.listContacts(arbi)
  const contactMatch = contacts.find((c) => {
    if (isEmail) return c.email === ref
    const u = c.user as UserInfo | undefined
    return u?.external_id === ref || c.external_id === ref
  })
  if (contactMatch) {
    const u = contactMatch.user as Record<string, unknown> | undefined
    const extId = (u?.external_id as string) ?? (contactMatch.external_id as string)
    const pubKey = (u?.encryption_public_key as string) ?? ''
    if (pubKey) return { extId, pubKey }
  }

  // Agents: parent-owned child identities. Wrapped in try/catch because
  // workspace-scoped tokens can't list cross-workspace agents.
  try {
    const agents = await agentOps.listAgents(arbi)
    const agentMatch = agents.find((a) => (a as Record<string, unknown>)[matchKey] === ref)
    if (agentMatch) {
      return { extId: agentMatch.external_id, pubKey: agentMatch.encryption_public_key }
    }
  } catch {
    // Agent listing not available in this auth context — keep going.
  }

  // Workspace members: catches DMs to peers we share a workspace with but
  // haven't explicitly added as contacts.
  try {
    const wsUsers = await workspaceOps.listWorkspaceUsers(arbi)
    const wsMatch = wsUsers.find((wu) => (wu as Record<string, unknown>)[matchKey] === ref)
    if (wsMatch) {
      const u = wsMatch as Record<string, unknown>
      return {
        extId: u.external_id as string,
        pubKey: u.encryption_public_key as string,
      }
    }
  } catch {
    // Workspace-user listing not available — fall through to null.
  }

  return null
}

function rowMatchesFilters(
  row: DmRow,
  opts: { unread?: boolean; from?: string; since?: string; thread?: string; myExtId?: string }
): boolean {
  if (opts.unread && row.read) return false

  const sender = row.sender as UserInfo | null
  const recipient = row.recipient as UserInfo | null

  if (opts.from) {
    const needle = opts.from.toLowerCase()
    const senderMatch = sender?.email?.toLowerCase() === needle || sender?.external_id === opts.from
    const recipientMatch =
      recipient?.email?.toLowerCase() === needle || recipient?.external_id === opts.from
    if (!senderMatch && !recipientMatch) return false
  }

  if (opts.thread) {
    const needle = opts.thread.toLowerCase()
    // `--thread` is "show everything with this peer," in either direction.
    const peer = sender?.external_id === opts.myExtId ? recipient : sender
    const peerMatch = peer?.email?.toLowerCase() === needle || peer?.external_id === opts.thread
    if (!peerMatch) return false
  }

  if (opts.since) {
    const cutoff = Date.parse(opts.since)
    if (Number.isFinite(cutoff)) {
      const createdAt = typeof row.created_at === 'string' ? Date.parse(row.created_at) : NaN
      if (!Number.isFinite(createdAt) || createdAt < cutoff) return false
    }
  }

  return true
}

export function registerDmCommand(program: Command): void {
  const dm = program.command('dm').description('Direct messages (E2E encrypted)')

  dm.command('list')
    .description('List direct messages (decrypted, newest first)')
    .option('--unread', 'Only show unread messages')
    .option('--from <email-or-extid>', 'Filter by sender OR recipient (email or usr- id)')
    .option(
      '--thread <email-or-extid>',
      'Show the full conversation with one peer (both directions)'
    )
    .option('--since <iso>', 'Only messages newer than this ISO timestamp')
    .option('-l, --limit <n>', 'Cap to N most-recent messages', (v) => parseInt(v, 10))
    .option('--json', 'Output as JSON (recommended for scripting / agents)')
    .action((opts: Record<string, unknown>) =>
      runAction(async () => {
        const { arbi, crypto } = await resolveDmCrypto()
        const all = (await dmOps.listDecryptedDMs(arbi, crypto)) as DmRow[]

        // `arbi dm` is the DM inbox view — the underlying endpoint also returns
        // workspaceuser_* / contact_accepted notifications, which have no place
        // here. (`arbi notifications` for the firehose, once that lands.)
        // session.userExtId is `string | null`; the filter helper wants
        // `string | undefined`. Coerce here so both shapes mean "no peer
        // to test against."
        const myExtId = arbi.session.getState().userExtId ?? undefined
        const dms = all.filter((r) => (r.type as string) === 'user_message')

        const filterOpts = {
          unread: Boolean(opts.unread),
          from: typeof opts.from === 'string' ? opts.from : undefined,
          thread: typeof opts.thread === 'string' ? opts.thread : undefined,
          since: typeof opts.since === 'string' ? opts.since : undefined,
          myExtId,
        }
        const filtered = dms.filter((r) => rowMatchesFilters(r, filterOpts))
        const limited =
          typeof opts.limit === 'number' && opts.limit > 0
            ? filtered.slice(0, opts.limit)
            : filtered

        if (opts.json) {
          // Re-tag every row with `encrypted_in_transit: true` — the SDK
          // verifies decryption end-to-end before returning, so the row's
          // mere presence in this list is proof. Surfacing it explicitly
          // matches SKILL.md's contract and lets security review grep for it.
          printJson(limited.map((r) => ({ ...r, encrypted_in_transit: true })))
          return
        }

        if (limited.length === 0) {
          // Status, not data. Goes to stderr so `arbi dm list | wc -l` is
          // accurate when there are no messages.
          process.stderr.write('No messages found.\n')
          return
        }

        const peer = (r: DmRow) => {
          const s = r.sender as UserInfo | null
          const recip = r.recipient as UserInfo | null
          return s?.external_id === myExtId ? recip : s
        }

        printTable(
          [
            { header: 'ID', width: 16, value: (r) => r.external_id as string },
            {
              header: 'DIR',
              width: 4,
              value: (r) => ((r.sender as UserInfo | null)?.external_id === myExtId ? '→' : '←'),
            },
            {
              header: 'PEER',
              width: 26,
              value: (r) => {
                const p = peer(r)
                return formatUserName(p) || p?.email || ''
              },
            },
            { header: 'READ', width: 6, value: (r) => (r.read ? 'yes' : 'no') },
            {
              header: 'CONTENT',
              width: 50,
              value: (r) => truncate((r.content as string) ?? '', 49),
            },
          ],
          limited
        )
      })()
    )

  dm.command('send [recipient] [content...]')
    .description('Send an E2E encrypted DM (interactive if no args)')
    .action((recipient?: string, contentParts?: string[]) =>
      runAction(async () => {
        const { arbi, crypto } = await resolveDmCrypto()

        // Pick recipient from contacts if not given
        if (!recipient) {
          requireInteractive(
            'Pass recipient + message as arguments: arbi dm send <email-or-id> "<message>"'
          )
          const contacts = await contactOps.listContacts(arbi)
          if (contacts.length === 0) {
            error('No contacts found. Add contacts first: arbi contacts add <email>')
            process.exit(1)
          }
          recipient = await promptSelect(
            'Send to',
            contacts.map((c) => {
              const u = c.user as UserInfo | null
              const name = formatUserName(u)
              return {
                name: name ? `${name} (${c.email})` : (c.email as string),
                value: u?.external_id ?? (c.external_id as string),
                description: c.email as string,
              }
            })
          )
        }

        // Join variadic content parts or prompt interactively
        let content = contentParts?.length ? contentParts.join(' ') : undefined
        if (!content) {
          requireInteractive(
            'Pass the message as a positional argument: arbi dm send <email> "your message"'
          )
          content = await promptInput('Message')
        }

        // Resolve recipient ext_id + encryption public key from one of three
        // sources (contacts, agents, workspace members). The old code did the
        // same six-way search twice (once for email input, once for ext_id);
        // collapsing into one helper removed ~80 LOC and a stack of subtle
        // branching bugs.
        const resolved = await resolveRecipient(arbi, recipient)
        if (!resolved) {
          error(
            `No contact, agent, or workspace member found for: ${recipient}\n` +
              `Try: arbi contacts add ${recipient.includes('@') ? recipient : '<their-email>'}`
          )
          // Distinct exit code so callers can branch "fix the reference" (3)
          // vs "auth missing" (2) vs "server fault" (1).
          process.exit(3)
        }
        const recipientExtId = resolved.extId
        const recipientPubKey = resolved.pubKey

        if (!recipientPubKey) {
          error(
            'Cannot send encrypted DM — recipient public key not found.\n' +
              'Add them as a contact first: arbi contacts add <email>'
          )
          process.exit(3)
        }

        const data = await dmOps.sendEncryptedDM(
          arbi,
          [
            {
              recipient_ext_id: recipientExtId,
              content,
              recipient_encryption_public_key: recipientPubKey,
            },
          ],
          crypto
        )
        // The text confirmation carries the security receipt: "(encrypted)"
        // makes the encrypted_in_transit invariant visible without needing
        // --json. Agents extract the notification ID with grep -oE 'ntf-…'.
        for (const n of data) {
          success(`Sent (encrypted): ${n.external_id} → ${n.recipient.email}`)
        }
      })()
    )

  dm.command('read [ids...]')
    .description('Mark messages as read (interactive picker if no IDs given)')
    .option('--all', 'Mark every unread message as read (no picker)')
    .action((ids?: string[], opts?: Record<string, unknown>) =>
      runAction(async () => {
        const { arbi, crypto } = await resolveDmCrypto()
        let msgIds = ids && ids.length > 0 ? ids : undefined

        if (!msgIds && opts?.all) {
          const data = await dmOps.listDecryptedDMs(arbi, crypto)
          msgIds = data.filter((m) => !m.read).map((m) => m.external_id as string)
        }

        if (!msgIds) {
          const data = await dmOps.listDecryptedDMs(arbi, crypto)
          const unread = data.filter((m) => !m.read)
          if (unread.length === 0) {
            process.stderr.write('No unread messages.\n')
            return
          }
          requireInteractive('Pass IDs directly or use --all to mark every unread message read.')
          msgIds = await promptCheckbox(
            'Select messages to mark as read',
            unread.map((m) => {
              const s = m.sender as UserInfo | null
              const from = formatUserName(s) || s?.email || ''
              return {
                name: `${from}: ${((m.content as string) ?? '').slice(0, 50)}`,
                value: m.external_id as string,
              }
            })
          )
          if (msgIds.length === 0) return
        }

        if (msgIds.length === 0) {
          process.stderr.write('No unread messages.\n')
          return
        }
        const data = await dmOps.markRead(arbi, msgIds)
        success(`Marked ${data.length} message(s) as read.`)
      })()
    )

  dm.command('delete [ids...]')
    .description('Delete messages (interactive picker if no IDs given)')
    .option('--all', 'Delete every message in your DM history (no picker, no prompt)')
    .option('--dry-run', 'Preview which messages would be deleted (no SDK call)')
    .action((ids?: string[], opts?: Record<string, unknown>) =>
      runAction(async () => {
        const { arbi, crypto } = await resolveDmCrypto()
        let msgIds = ids && ids.length > 0 ? ids : undefined

        if (!msgIds && opts?.all) {
          const data = await dmOps.listDecryptedDMs(arbi, crypto)
          msgIds = data.map((m) => m.external_id as string)
        }

        if (!msgIds) {
          const data = await dmOps.listDecryptedDMs(arbi, crypto)
          if (data.length === 0) {
            process.stderr.write('No messages found.\n')
            return
          }
          requireInteractive('Pass IDs directly or use --all to delete the whole inbox.')
          msgIds = await promptCheckbox(
            'Select messages to delete',
            data.map((m) => {
              const s = m.sender as UserInfo | null
              const from = formatUserName(s) || s?.email || ''
              return {
                name: `${from}: ${((m.content as string) ?? '').slice(0, 50)}`,
                value: m.external_id as string,
              }
            })
          )
          if (msgIds.length === 0) return
        }
        if (msgIds.length === 0) {
          process.stderr.write('No messages to delete.\n')
          return
        }
        if (opts?.dryRun) {
          dryRun(`delete ${msgIds.length} message(s)`, msgIds)
          return
        }
        await dmOps.deleteDMs(arbi, msgIds)
        success(`Deleted ${msgIds.length} message(s).`)
      })()
    )

  // Default action: list, OR suggest a subcommand if user typed a typo
  // ("arbi dm lst" used to return "too many arguments for 'dm'" with no hint
  // because commander treated `lst` as a positional arg, not a subcommand).
  dm.arguments('[maybeSubcommand]').action(async (maybe?: string) => {
    if (maybe) {
      suggestSubcommandAndExit(
        'dm',
        maybe,
        dm.commands.map((c) => c.name())
      )
    }
    await dm.commands.find((c) => c.name() === 'list')!.parseAsync([], { from: 'user' })
  })
}
